import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
    Button,
    Chip,
    Snackbar,
    Alert,
    useTheme,
    Stack,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import AdminLogin from './AdminLogin';
import { isUserAdmin, getAllOrganizers, createTournament, createOrganizer, updateTournament, updateOrganizer, createRegularTournament, updateRegularTournament } from '../../firebase/firestore';
import AdminInitializer from './AdminInitializer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import AddTournamentForm from './AddTournamentForm';
import AddOrganizerForm from './AddOrganizerForm';
import AddRegularTournamentForm from './AddRegularTournamentForm';
import EditFeaturedEventForm from './EditFeaturedEventForm';
import TournamentList from './TournamentList';
import RegularTournamentList from './RegularTournamentList';
import OrganizerList from './OrganizerList';
import LimitedTierlistManager from './LimitedTierlistManager';
import UserManagement from './UserManagement';

function AdminPanel() {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const { currentUser, userData, loading, logout } = useAuth();
    const [adminVerified, setAdminVerified] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [regularTournamentDialog, setRegularTournamentDialog] = useState(false);
    const [organizerDialog, setOrganizerDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [organizers, setOrganizers] = useState([]);
    const [newOrganizer, setNewOrganizer] = useState({
        name: '',
        address: '',
        coordinates: { lat: '', lng: '' },
        website: ''
    });
    const [newTournament, setNewTournament] = useState({
        name: '',
        date: '',
        time: '',
        duration: 1,
        city: '',
        price: '',
        url: '',
        meleeLink: '',
        format: 'Core Constructed',
        structure: '',
        rank: 'REGIONAL',
        organizer_id: '',
        organizer: null,
        prizes: [],
        customLocation: null
    });
    const [newRegularTournament, setNewRegularTournament] = useState({
        name: '',
        dayOfWeek: '',
        time: '',
        duration: 1,
        city: '',
        price: '',
        url: '',
        meleeLink: '',
        format: 'Core Constructed',
        structure: '',
        rank: 'REGIONAL',
        organizer_id: '',
        organizer: null,
        prizes: [],
        customLocation: null
    });
    const [editingTournament, setEditingTournament] = useState(null);
    const [editingRegularTournament, setEditingRegularTournament] = useState(null);
    const [tournamentUpdated, setTournamentUpdated] = useState(false);
    const [regularTournamentUpdated, setRegularTournamentUpdated] = useState(false);
    const [editingOrganizer, setEditingOrganizer] = useState(null);
    const [featuredEventDialog, setFeaturedEventDialog] = useState(false);

    useEffect(() => {
        const verifyAdminStatus = async () => {
            if (currentUser && userData) {
                try {
                    // Sprawdź czy użytkownik ma rolę admin lub superadmin
                    const isAdmin = userData.role === 'admin' || userData.role === 'superadmin';
                    setAdminVerified(isAdmin);

                    if (isAdmin) {
                        const orgs = await getAllOrganizers();
                        setOrganizers(orgs);
                    }
                } catch (err) {
                    console.error("Błąd podczas weryfikacji statusu administratora:", err);
                    setError("Nie udało się zweryfikować statusu administratora");
                } finally {
                    setCheckingAdmin(false);
                }
            } else if (!currentUser) {
                setCheckingAdmin(false);
            }
        };

        verifyAdminStatus();
    }, [currentUser, userData]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleLogout = () => {
        logout();
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTournament(null);
        setNewTournament({
            name: '',
            date: '',
            time: '',
            duration: 1,
            city: '',
            price: '',
            url: '',
            meleeLink: '',
            format: 'Core Constructed',
            structure: '',
            rank: 'REGIONAL',
            organizer_id: '',
            organizer: null,
            prizes: [],
            customLocation: null
        });
    };

    const handleOpenOrganizerDialog = () => {
        setOrganizerDialog(true);
    };

    const handleCloseOrganizerDialog = () => {
        setOrganizerDialog(false);
        setEditingOrganizer(null);
        setNewOrganizer({
            name: '',
            address: '',
            coordinates: { lat: '', lng: '' },
            website: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Obsługa zagnieżdżonych pól (np. customLocation.name)
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setNewTournament(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setNewTournament(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleOrganizerSelect = (event, value) => {
        setNewTournament(prev => ({
            ...prev,
            organizer: value,
            organizer_id: value?.id || ''
        }));
    };

    const handleNewOrganizerChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setNewOrganizer(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setNewOrganizer(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAddPrize = () => {
        setNewTournament(prev => ({
            ...prev,
            prizes: [...(Array.isArray(prev.prizes) ? prev.prizes : []), {
                position: prev.prizes?.length === 0 ? "Dodatkowy opis" : "",
                description: "",
                order: prev.prizes?.length || 0
            }]
        }));
    };

    const handlePrizeChange = (index, field, value) => {
        setNewTournament(prev => {
            const newPrizes = [...(Array.isArray(prev.prizes) ? prev.prizes : [])];
            newPrizes[index] = {
                ...newPrizes[index],
                [field]: value
            };
            return {
                ...prev,
                prizes: newPrizes
            };
        });
    };

    const handleRemovePrize = (index) => {
        setNewTournament(prev => {
            const newPrizes = [...(Array.isArray(prev.prizes) ? prev.prizes : [])];
            newPrizes.splice(index, 1);
            return {
                ...prev,
                prizes: newPrizes
            };
        });
    };

    const handleEditOrganizer = (organizer) => {
        setNewOrganizer({
            name: organizer.name,
            address: organizer.address,
            coordinates: organizer.coordinates || { lat: '', lng: '' },
            website: organizer.website || ''
        });
        setEditingOrganizer(organizer);
        setOrganizerDialog(true);
    };

    const handleSubmitOrganizer = async () => {
        try {
            if (!newOrganizer.name || !newOrganizer.address) {
                setSnackbar({
                    open: true,
                    message: 'Proszę wypełnić wymagane pola (nazwa, adres)',
                    severity: 'error'
                });
                return;
            }

            if (editingOrganizer) {
                await updateOrganizer(editingOrganizer.id, newOrganizer);
                setSnackbar({
                    open: true,
                    message: 'Organizator został pomyślnie zaktualizowany!',
                    severity: 'success'
                });
            } else {
                const organizerId = await createOrganizer(newOrganizer);
                setSnackbar({
                    open: true,
                    message: 'Organizator został pomyślnie dodany!',
                    severity: 'success'
                });

                setNewTournament(prev => ({
                    ...prev,
                    organizer: { ...newOrganizer, id: organizerId },
                    organizer_id: organizerId
                }));
            }

            const orgs = await getAllOrganizers();
            setOrganizers(orgs);

            setNewOrganizer({
                name: '',
                address: '',
                coordinates: { lat: '', lng: '' },
                website: ''
            });
            setOrganizerDialog(false);
            setEditingOrganizer(null);
        } catch (error) {
            console.error('Błąd podczas zapisywania organizatora:', error);
            setSnackbar({
                open: true,
                message: 'Wystąpił błąd podczas zapisywania organizatora',
                severity: 'error'
            });
        }
    };

    const handleSubmit = async () => {
        try {
            if (!newTournament.name || !newTournament.date || !newTournament.city || !newTournament.organizer_id) {
                setSnackbar({
                    open: true,
                    message: 'Proszę wypełnić wymagane pola (nazwa, data, miasto, organizator)',
                    severity: 'error'
                });
                return;
            }

            // Sortuj nagrody według kolejności przed zapisem
            const sortedPrizes = newTournament.prizes
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(({ position, description }) => ({
                    position,
                    description
                }));

            const tournamentData = {
                ...newTournament,
                prizes: sortedPrizes,
                customLocation: newTournament.customLocation || null
            };

            delete tournamentData.organizer;

            if (editingTournament) {
                await updateTournament(editingTournament.id, tournamentData);
                setSnackbar({
                    open: true,
                    message: 'Turniej został pomyślnie zaktualizowany!',
                    severity: 'success'
                });
            } else {
                await createTournament(tournamentData);
                setSnackbar({
                    open: true,
                    message: 'Turniej został pomyślnie dodany!',
                    severity: 'success'
                });
            }

            setNewTournament({
                name: '',
                date: '',
                time: '',
                duration: 1,
                city: '',
                price: '',
                url: '',
                meleeLink: '',
                format: 'Core Constructed',
                structure: '',
                rank: 'REGIONAL',
                organizer_id: '',
                organizer: null,
                prizes: [],
                customLocation: null
            });
            setOpenDialog(false);
            setEditingTournament(null);
            setTournamentUpdated(prev => !prev);
        } catch (error) {
            console.error('Błąd podczas zapisywania turnieju:', error);
            setSnackbar({
                open: true,
                message: 'Wystąpił błąd podczas zapisywania turnieju',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleEditTournament = (tournament) => {
        const prizesArray = tournament.prizes ?
            (Array.isArray(tournament.prizes) ?
                tournament.prizes.map((prize, index) => ({
                    ...prize,
                    order: index
                })) :
                Object.entries(tournament.prizes).map(([position, description], index) => ({
                    position,
                    description,
                    order: index
                }))
            ) : [];

        const tournamentToEdit = {
            ...tournament,
            prizes: prizesArray,
            date: new Date(tournament.date).toISOString().split('T')[0],
            customLocation: tournament.customLocation || null,
            duration: tournament.duration || 1
        };

        setNewTournament(tournamentToEdit);
        setEditingTournament(tournament);
        setOpenDialog(true);
    };

    const handleOpenRegularTournamentDialog = () => {
        setRegularTournamentDialog(true);
    };

    const handleCloseRegularTournamentDialog = () => {
        setRegularTournamentDialog(false);
        setEditingRegularTournament(null);
        setNewRegularTournament({
            name: '',
            dayOfWeek: '',
            time: '',
            duration: 1,
            city: '',
            price: '',
            url: '',
            meleeLink: '',
            format: 'Core Constructed',
            structure: '',
            rank: 'REGIONAL',
            organizer_id: '',
            organizer: null,
            prizes: [],
            customLocation: null
        });
    };

    const handleRegularTournamentInputChange = (e) => {
        const { name, value } = e.target;

        console.log('Zmiana pola turnieju regularnego:', name, value);

        // Obsługa zagnieżdżonych pól (np. customLocation.name)
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setNewRegularTournament(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setNewRegularTournament(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleRegularTournamentOrganizerSelect = (event, value) => {
        setNewRegularTournament(prev => ({
            ...prev,
            organizer: value,
            organizer_id: value?.id || ''
        }));
    };

    const handleAddRegularTournamentPrize = () => {
        setNewRegularTournament(prev => ({
            ...prev,
            prizes: [...prev.prizes, { position: '', description: '', order: prev.prizes.length }]
        }));
    };

    const handleRegularTournamentPrizeChange = (index, field, value) => {
        setNewRegularTournament(prev => ({
            ...prev,
            prizes: prev.prizes.map((prize, i) =>
                i === index ? { ...prize, [field]: value } : prize
            )
        }));
    };

    const handleRemoveRegularTournamentPrize = (index) => {
        setNewRegularTournament(prev => ({
            ...prev,
            prizes: prev.prizes.filter((_, i) => i !== index).map((prize, i) => ({
                ...prize,
                order: i
            }))
        }));
    };

    const handleRegularTournamentSubmit = async () => {
        try {
            console.log('Rozpoczynanie zapisywania turnieju regularnego:', newRegularTournament);
            console.log('Wybrany dzień tygodnia:', newRegularTournament.dayOfWeek);

            // Walidacja
            if (!newRegularTournament.name || !newRegularTournament.dayOfWeek || !newRegularTournament.time || !newRegularTournament.city || !newRegularTournament.url || !newRegularTournament.organizer_id) {
                console.log('Błąd walidacji - brakujące pola:', {
                    name: !!newRegularTournament.name,
                    dayOfWeek: !!newRegularTournament.dayOfWeek,
                    time: !!newRegularTournament.time,
                    city: !!newRegularTournament.city,
                    url: !!newRegularTournament.url,
                    organizer_id: !!newRegularTournament.organizer_id
                });
                setSnackbar({
                    open: true,
                    message: 'Proszę wypełnić wszystkie wymagane pola!',
                    severity: 'error'
                });
                return;
            }

            // Sortuj nagrody według kolejności przed zapisem
            const sortedPrizes = newRegularTournament.prizes
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(({ position, description }) => ({
                    position,
                    description
                }));

            const regularTournamentData = {
                ...newRegularTournament,
                prizes: sortedPrizes,
                customLocation: newRegularTournament.customLocation || null
            };

            delete regularTournamentData.organizer;

            console.log('Dane do zapisu:', regularTournamentData);
            console.log('Dzień tygodnia do zapisu:', regularTournamentData.dayOfWeek);

            if (editingRegularTournament) {
                console.log('Aktualizacja turnieju regularnego:', editingRegularTournament.id);
                await updateRegularTournament(editingRegularTournament.id, regularTournamentData);
                setSnackbar({
                    open: true,
                    message: 'Turniej regularny został pomyślnie zaktualizowany!',
                    severity: 'success'
                });
            } else {
                console.log('Tworzenie nowego turnieju regularnego');
                const result = await createRegularTournament(regularTournamentData);
                console.log('Turniej regularny utworzony z ID:', result);
                setSnackbar({
                    open: true,
                    message: 'Turniej regularny został pomyślnie dodany!',
                    severity: 'success'
                });
            }

            setNewRegularTournament({
                name: '',
                dayOfWeek: '',
                time: '',
                duration: 1,
                city: '',
                price: '',
                url: '',
                meleeLink: '',
                format: 'Core Constructed',
                structure: '',
                rank: 'REGIONAL',
                organizer_id: '',
                organizer: null,
                prizes: [],
                customLocation: null
            });
            setRegularTournamentDialog(false);
            setEditingRegularTournament(null);
            setRegularTournamentUpdated(prev => !prev);
        } catch (error) {
            console.error('Szczegółowy błąd podczas zapisywania turnieju regularnego:', error);
            console.error('Stack trace:', error.stack);
            setSnackbar({
                open: true,
                message: `Wystąpił błąd podczas zapisywania turnieju regularnego: ${error.message}`,
                severity: 'error'
            });
        }
    };

    const handleEditRegularTournament = (regularTournament) => {
        setEditingRegularTournament(regularTournament);
        setNewRegularTournament({
            name: regularTournament.name,
            dayOfWeek: regularTournament.dayOfWeek,
            time: regularTournament.time,
            duration: regularTournament.duration || 1,
            city: regularTournament.city,
            price: regularTournament.price,
            url: regularTournament.url,
            meleeLink: regularTournament.meleeLink,
            format: regularTournament.format,
            structure: regularTournament.structure,
            rank: regularTournament.rank,
            organizer_id: regularTournament.organizer_id,
            organizer: regularTournament.organizer,
            prizes: regularTournament.prizes || [],
            customLocation: regularTournament.customLocation
        });
        setRegularTournamentDialog(true);
    };

    const handleOpenFeaturedEventDialog = () => {
        setFeaturedEventDialog(true);
    };

    const handleCloseFeaturedEventDialog = () => {
        setFeaturedEventDialog(false);
    };

    const handleFeaturedEventSuccess = (message) => {
        setSnackbar({
            open: true,
            message: message,
            severity: 'success'
        });
    };

    if (loading || checkingAdmin) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!currentUser) {
        return <AdminLogin />;
    }

    if (!adminVerified) {
        return <AdminInitializer onAdminCreated={() => setAdminVerified(true)} />;
    }

    return (
        <Container maxWidth="xl" sx={{
            mt: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 4 },
            px: { xs: 1, sm: 2 }
        }}>
            {/* Kompaktowy header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                mb: 2,
                backgroundColor: 'rgba(76, 29, 149, 0.08)',
                padding: { xs: '0.75rem', sm: '1rem' },
                borderRadius: '0.5rem',
                border: '1px solid rgba(76, 29, 149, 0.15)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5" component="h1" sx={{
                        fontWeight: '700',
                        color: 'white',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}>
                        Panel Admin
                    </Typography>
                    <Chip
                        label={currentUser.email}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                            color: 'rgb(196, 181, 253)',
                            border: '1px solid rgba(76, 29, 149, 0.3)',
                            fontSize: '0.75rem',
                            height: '24px'
                        }}
                    />
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Tooltip title="Dodaj turniej">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenDialog}
                            startIcon={<AddIcon />}
                            sx={{
                                backgroundColor: 'rgb(76, 29, 149)',
                                '&:hover': { backgroundColor: 'rgb(91, 33, 182)' },
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.5
                            }}
                        >
                            Turniej
                        </Button>
                    </Tooltip>
                    <Tooltip title="Dodaj turniej regularny">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenRegularTournamentDialog}
                            startIcon={<AddIcon />}
                            sx={{
                                backgroundColor: 'rgb(34, 197, 94)',
                                '&:hover': { backgroundColor: 'rgb(22, 163, 74)' },
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.5
                            }}
                        >
                            Regularny
                        </Button>
                    </Tooltip>
                    <Tooltip title="Edytuj featured event">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenFeaturedEventDialog}
                            startIcon={<EditIcon />}
                            sx={{
                                backgroundColor: 'rgb(245, 158, 11)',
                                '&:hover': { backgroundColor: 'rgb(217, 119, 6)' },
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.5
                            }}
                        >
                            Featured
                        </Button>
                    </Tooltip>
                    <Tooltip title="Wyloguj">
                        <IconButton
                            size="small"
                            onClick={handleLogout}
                            sx={{
                                color: 'rgb(239, 68, 68)',
                                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Kompaktowe taby */}
            <Paper sx={{
                borderRadius: '0.5rem',
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                border: '1px solid rgb(55, 65, 81)',
                mb: 2
            }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    textColor="inherit"
                    TabIndicatorProps={{
                        style: { backgroundColor: 'rgb(196, 181, 253)' }
                    }}
                    sx={{
                        '& .MuiTab-root': {
                            color: 'rgb(196, 181, 253)',
                            fontSize: '0.875rem',
                            py: 1,
                            minHeight: '48px',
                            '&.Mui-selected': { color: 'white' }
                        }
                    }}
                >
                    <Tab label="Turnieje" />
                    <Tab label="Turnieje Regularne" />
                    <Tab label="Organizatorzy" />
                    <Tab label="Tierlisty Limited" />
                    <Tab label="Użytkownicy" />
                </Tabs>
            </Paper>

            {/* Zawartość tabów */}
            <Box sx={{ minHeight: '400px' }}>
                {tabValue === 0 && (
                    <TournamentList
                        onEdit={handleEditTournament}
                        onTournamentUpdated={tournamentUpdated}
                    />
                )}
                {tabValue === 1 && (
                    <RegularTournamentList
                        onEdit={handleEditRegularTournament}
                        onRegularTournamentUpdated={regularTournamentUpdated}
                    />
                )}
                {tabValue === 2 && (
                    <OrganizerList onEdit={handleEditOrganizer} />
                )}
                {tabValue === 3 && (
                    <LimitedTierlistManager />
                )}
                {tabValue === 4 && (
                    <UserManagement />
                )}
            </Box>

            {/* Formularze */}
            <AddTournamentForm
                open={openDialog}
                onClose={handleCloseDialog}
                onSubmit={handleSubmit}
                newTournament={newTournament}
                handleInputChange={handleInputChange}
                handleOrganizerSelect={handleOrganizerSelect}
                organizers={organizers}
                handleAddPrize={handleAddPrize}
                handlePrizeChange={handlePrizeChange}
                handleRemovePrize={handleRemovePrize}
                handleOpenOrganizerDialog={handleOpenOrganizerDialog}
                isEditing={!!editingTournament}
            />

            <AddOrganizerForm
                open={organizerDialog}
                onClose={handleCloseOrganizerDialog}
                onSubmit={handleSubmitOrganizer}
                newOrganizer={newOrganizer}
                handleNewOrganizerChange={handleNewOrganizerChange}
                isEditing={!!editingOrganizer}
            />

            <AddRegularTournamentForm
                open={regularTournamentDialog}
                onClose={handleCloseRegularTournamentDialog}
                onSubmit={handleRegularTournamentSubmit}
                newRegularTournament={newRegularTournament}
                handleInputChange={handleRegularTournamentInputChange}
                handleOrganizerSelect={handleRegularTournamentOrganizerSelect}
                organizers={organizers}
                handleAddPrize={handleAddRegularTournamentPrize}
                handlePrizeChange={handleRegularTournamentPrizeChange}
                handleRemovePrize={handleRemoveRegularTournamentPrize}
                handleOpenOrganizerDialog={handleOpenOrganizerDialog}
                isEditing={!!editingRegularTournament}
            />

            <EditFeaturedEventForm
                open={featuredEventDialog}
                onClose={handleCloseFeaturedEventDialog}
                onSuccess={handleFeaturedEventSuccess}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AdminPanel;